/**
 * Strategic AI gap analysis — UI for /functions/v1/competitor-gaps.
 *
 * Pre-aggregates 30d stats per account client-side (cheap, deterministic),
 * ships to Claude via Supabase Edge Function, renders 4-6 strategic gaps
 * + 2-3 strengths. Sits above the tactical "Why It Worked" card on
 * /competitive — gaps is "where to play", why-it-worked is "how to play".
 */
import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowDownRight,
  ArrowUpRight,
  History,
  Loader2,
  RefreshCw,
  Sparkles,
  Target,
  TrendingUp,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { marketing } from '@/lib/supabase/marketing';
import { cn } from '@/lib/utils';
import type {
  IgCompetitor,
  IgCompetitorPost,
  IgCompetitorSnapshot,
} from '@/hooks/useCompetitors';
import {
  useCompetitiveGapReports,
  useCompetitiveGapReport,
} from '@/hooks/useCompetitiveGapReports';

interface Props {
  competitors: IgCompetitor[];
  snapshots: IgCompetitorSnapshot[];
  posts: IgCompetitorPost[];
}

interface Gap {
  dimension: string;
  title: string;
  flaxValue: string;
  cohortValue: string;
  delta: string;
  severity: 'high' | 'medium' | 'low';
  evidence: string;
  monthlyAction: string;
  expectedImpact: string;
}

interface Strength {
  title: string;
  evidence: string;
}

interface GapsResponse {
  headline: string;
  gaps: Gap[];
  strengths: Strength[];
  generatedAt?: string;
  partial?: boolean;
  fallback?: string;
  error?: string;
}

const WINDOW_DAYS = 30;
const WINDOW_MS = WINDOW_DAYS * 24 * 60 * 60 * 1000;

export function CompetitiveGaps({ competitors, snapshots, posts }: Props) {
  const [force, setForce] = useState(false);
  const [selectedReportId, setSelectedReportId] = useState<string | null>(null);
  const qc = useQueryClient();

  const followersByHandle = useMemo(() => {
    const latest = new Map<string, IgCompetitorSnapshot>();
    for (const s of snapshots) {
      const prev = latest.get(s.username);
      if (!prev || (s.snapshot_date ?? '') > (prev.snapshot_date ?? '')) latest.set(s.username, s);
    }
    const map = new Map<string, number>();
    for (const [h, s] of latest) map.set(h, s.followers_count ?? 0);
    return map;
  }, [snapshots]);

  const payload = useMemo(
    () => buildPayload(competitors, followersByHandle, posts),
    [competitors, followersByHandle, posts],
  );

  const history = useCompetitiveGapReports(20);
  const pastReport = useCompetitiveGapReport(selectedReportId);

  const gaps = useMutation({
    mutationFn: async (): Promise<GapsResponse> => {
      if (!payload) throw new Error('No Flax data or competitors in window');
      const { data, error } = await marketing.functions.invoke<GapsResponse>(
        force ? 'competitor-gaps?force=1' : 'competitor-gaps',
        { body: payload },
      );
      if (error) throw new Error(error.message);
      if (!data) throw new Error('empty response');
      if (data.error) throw new Error(data.error);
      return data;
    },
    onSuccess: () => {
      setSelectedReportId(null);
      qc.invalidateQueries({ queryKey: ['competitive-gap-reports'] });
    },
  });

  const displayed: GapsResponse | null = pastReport.data
    ? {
        headline: pastReport.data.response_json.headline ?? '',
        gaps: (pastReport.data.response_json.gaps ?? []) as Gap[],
        strengths: (pastReport.data.response_json.strengths ?? []) as Strength[],
        generatedAt: pastReport.data.generated_at,
      }
    : gaps.data ?? null;

  if (!payload || payload.competitors.length === 0) {
    return (
      <div className="rounded-md border border-border/40 bg-muted/30 p-6 text-center text-sm text-muted-foreground">
        Need Flax + at least 1 competitor with posts in the last {WINDOW_DAYS} days.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-md border border-border/40 bg-gradient-to-br from-emerald-500/5 to-sky-500/5 p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-2xs text-muted-foreground">
              <Target className="h-3 w-3 text-emerald-500" />
              Strategic gap analysis · last {WINDOW_DAYS} days · {payload.competitors.length} rivals
              {selectedReportId && (
                <Badge variant="muted" className="ml-1 bg-amber-500/15 text-amber-700 dark:text-amber-400">
                  viewing archived
                </Badge>
              )}
            </div>
            <h3 className="mt-1 text-lg font-semibold leading-tight">
              {displayed?.headline ?? 'Where Flax under-indexes the cohort'}
            </h3>
            {displayed?.generatedAt && (
              <p className="mt-1 text-2xs text-muted-foreground">
                Generated{' '}
                {new Date(displayed.generatedAt).toLocaleString('en-IN', {
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
                {displayed.partial && ' · partial response'}
              </p>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {history.data && history.data.length > 0 && (
              <PastReportPicker
                reports={history.data}
                selectedId={selectedReportId}
                onSelect={setSelectedReportId}
              />
            )}
            <button
              type="button"
              onClick={() => {
                setSelectedReportId(null);
                // "New report" always bypasses the day-long ai_cache so we
                // get a fresh Claude call — and the persistence hook fires,
                // dropping a row into marketing.competitive_gap_report.
                setForce(true);
                gaps.mutate();
              }}
              disabled={gaps.isPending}
              className={cn(
                'inline-flex shrink-0 items-center gap-1.5 rounded-md border border-border/60 bg-card px-3 py-1.5 text-xs font-medium',
                'hover:bg-accent disabled:opacity-50',
              )}
            >
              {gaps.isPending ? (
                <>
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Analyzing…
                </>
              ) : displayed ? (
                <>
                  <RefreshCw className="h-3 w-3" />
                  New report
                </>
              ) : (
                <>
                  <Sparkles className="h-3 w-3 text-emerald-500" />
                  Generate
                </>
              )}
            </button>
          </div>
        </div>

        {gaps.isError && (
          <div className="mt-3 rounded-md border border-rose-500/40 bg-rose-500/10 p-2 text-2xs text-rose-600">
            {(gaps.error as Error).message}
          </div>
        )}
        {pastReport.isLoading && selectedReportId && (
          <div className="mt-3 flex items-center gap-2 text-2xs text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" />
            Loading archived report…
          </div>
        )}
      </div>

      {displayed?.gaps && displayed.gaps.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Gaps to close
          </h4>
          <div className="grid gap-3 lg:grid-cols-2">
            {displayed.gaps.map((g, idx) => (
              <GapCard key={idx} gap={g} />
            ))}
          </div>
        </div>
      )}

      {displayed?.strengths && displayed.strengths.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Strengths to defend
          </h4>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {displayed.strengths.map((s, idx) => (
              <StrengthCard key={idx} strength={s} />
            ))}
          </div>
        </div>
      )}

      {displayed?.fallback && (
        <div className="rounded-md border border-border/40 bg-card p-4 text-sm whitespace-pre-wrap">
          {displayed.fallback}
        </div>
      )}
    </div>
  );
}

function PastReportPicker({
  reports,
  selectedId,
  onSelect,
}: {
  reports: Array<{ id: string; generated_at: string; headline: string | null; gap_count: number | null }>;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
}) {
  return (
    <div className="relative">
      <select
        value={selectedId ?? ''}
        onChange={(e) => onSelect(e.target.value || null)}
        className="appearance-none rounded-md border border-border/60 bg-card py-1.5 pl-8 pr-8 text-xs font-medium hover:bg-accent"
        title="Recall a past report"
      >
        <option value="">Latest run</option>
        {reports.map((r) => (
          <option key={r.id} value={r.id}>
            {new Date(r.generated_at).toLocaleString('en-IN', {
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })}
            {r.gap_count != null ? ` · ${r.gap_count} gaps` : ''}
          </option>
        ))}
      </select>
      <History className="pointer-events-none absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground" />
    </div>
  );
}

function GapCard({ gap }: { gap: Gap }) {
  const severityColor =
    gap.severity === 'high'
      ? 'bg-rose-500/15 text-rose-700 dark:text-rose-400'
      : gap.severity === 'medium'
        ? 'bg-amber-500/15 text-amber-700 dark:text-amber-400'
        : 'bg-slate-500/15 text-slate-700 dark:text-slate-400';

  return (
    <div className="rounded-md border border-border/40 bg-card p-4">
      <div className="flex items-start gap-2">
        <ArrowDownRight className="mt-0.5 h-4 w-4 shrink-0 text-rose-500" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Badge variant="muted" className="text-2xs">
              {gap.dimension}
            </Badge>
            <span className={cn('rounded px-1.5 py-0.5 text-2xs font-medium', severityColor)}>
              {gap.severity}
            </span>
          </div>
          <h5 className="mt-1.5 text-sm font-semibold leading-tight">{gap.title}</h5>
          <p className="mt-1 text-xs text-muted-foreground">{gap.evidence}</p>
        </div>
      </div>

      <dl className="mt-3 grid grid-cols-1 gap-x-3 gap-y-1.5 text-2xs">
        <Row label="Flax" value={gap.flaxValue} />
        <Row label="Cohort" value={gap.cohortValue} />
        <Row label="Delta" value={gap.delta} emphasize />
      </dl>

      <div className="mt-3 rounded-md border border-emerald-500/20 bg-emerald-500/5 p-3">
        <div className="flex items-center gap-1.5 text-2xs font-medium uppercase tracking-wide text-emerald-600 dark:text-emerald-400">
          <TrendingUp className="h-3 w-3" />
          This month
        </div>
        <p className="mt-1 text-xs leading-relaxed">{gap.monthlyAction}</p>
        <p className="mt-1.5 text-2xs text-muted-foreground">{gap.expectedImpact}</p>
      </div>
    </div>
  );
}

function StrengthCard({ strength }: { strength: Strength }) {
  return (
    <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3">
      <div className="flex items-start gap-2">
        <ArrowUpRight className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
        <div className="min-w-0">
          <h5 className="text-sm font-semibold leading-tight">{strength.title}</h5>
          <p className="mt-1 text-xs text-muted-foreground">{strength.evidence}</p>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, emphasize }: { label: string; value: string; emphasize?: boolean }) {
  return (
    <div className="flex gap-2">
      <dt className="w-14 shrink-0 text-muted-foreground">{label}</dt>
      <dd className={cn('text-foreground/90', emphasize && 'font-medium')}>{value}</dd>
    </div>
  );
}

/**
 * Pre-aggregate per-account stats across the last 30 days. Cheap to compute
 * client-side and saves a ton of tokens vs sending raw posts.
 */
function buildPayload(
  competitors: IgCompetitor[],
  followersByHandle: Map<string, number>,
  posts: IgCompetitorPost[],
) {
  const since = Date.now() - WINDOW_MS;
  const byHandle = new Map<string, IgCompetitorPost[]>();
  for (const p of posts) {
    if (!p.posted_at) continue;
    if (new Date(p.posted_at).getTime() < since) continue;
    const arr = byHandle.get(p.username) ?? [];
    arr.push(p);
    byHandle.set(p.username, arr);
  }

  const stats = (handle: string, followers: number) => {
    const rows = byHandle.get(handle) ?? [];
    if (rows.length === 0) return null;

    const dates = rows
      .map((r) => (r.posted_at ? new Date(r.posted_at).getTime() : 0))
      .filter(Boolean)
      .sort((a, b) => a - b);
    const spanDays =
      dates.length > 1
        ? Math.max(1, Math.round((dates[dates.length - 1]! - dates[0]!) / 86400000))
        : WINDOW_DAYS;

    const reels = rows.filter((r) => r.media_type === 'VIDEO');
    const carousels = rows.filter((r) => r.media_type === 'CAROUSEL_ALBUM');
    const images = rows.filter((r) => r.media_type === 'IMAGE' || (!r.media_type && r.image_url));

    const sumLikes = rows.reduce((s, r) => s + (r.like_count ?? 0), 0);
    const sumComments = rows.reduce((s, r) => s + (r.comments_count ?? 0), 0);
    const sumViews = rows.reduce((s, r) => s + (r.views ?? 0), 0);
    const avgLikes = sumLikes / rows.length;
    const avgComments = sumComments / rows.length;
    const avgViews = sumViews / rows.length;
    const engagementRatePct =
      followers > 0 ? ((avgLikes + avgComments) / followers) * 100 : 0;

    const captions = rows.map((r) => r.caption ?? '');
    const avgCaptionChars =
      captions.reduce((s, c) => s + c.length, 0) / Math.max(1, captions.length);

    const tagCounts = new Map<string, number>();
    let totalTags = 0;
    for (const c of captions) {
      const tags = c.match(/#[\w\d_]+/g) ?? [];
      totalTags += tags.length;
      for (const t of tags) {
        const k = t.toLowerCase();
        tagCounts.set(k, (tagCounts.get(k) ?? 0) + 1);
      }
    }
    const topHashtags = [...tagCounts.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([t]) => t);

    const topCaptionSamples = [...rows]
      .sort((a, b) => (b.like_count ?? 0) - (a.like_count ?? 0))
      .slice(0, 5)
      .map((r) => (r.caption ?? '').split('\n')[0]?.trim() ?? '')
      .filter(Boolean);

    const boostedCount = rows.filter((r) => r.is_boosted).length;

    return {
      username: handle,
      followers,
      postsTotal: rows.length,
      postsPerWeek: (rows.length * 7) / spanDays,
      reelPct: (reels.length / rows.length) * 100,
      carouselPct: (carousels.length / rows.length) * 100,
      imagePct: (images.length / rows.length) * 100,
      avgLikes,
      avgComments,
      avgViews,
      engagementRatePct,
      avgCaptionChars,
      avgHashtagsPerPost: totalTags / Math.max(1, rows.length),
      topHashtags,
      topCaptionSamples,
      boostedPostsPct: (boostedCount / rows.length) * 100,
    };
  };

  const flaxHandle = competitors.find((c) => c.is_us)?.username;
  if (!flaxHandle) return null;
  const flaxFollowers = followersByHandle.get(flaxHandle) ?? 0;
  const flax = stats(flaxHandle, flaxFollowers);
  if (!flax) return null;

  const competitorStats = competitors
    .filter((c) => !c.is_us && byHandle.has(c.username))
    .map((c) => stats(c.username, followersByHandle.get(c.username) ?? 0))
    .filter((s): s is NonNullable<typeof s> => s != null);

  return {
    windowDays: WINDOW_DAYS,
    flax,
    competitors: competitorStats,
  };
}
